import {
  presentationAtom,
  presentationHistoryAtom,
  presentationOriginalAtom,
  type PresentationData,
} from '@/atoms/slideBuilderAtoms';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';

const MAX_HISTORY = 50;

export function usePresentationHistory() {
  const [presentation, setPresentation] = useAtom(presentationAtom);
  const [history, setHistory] = useAtom(presentationHistoryAtom);
  const original = useAtomValue(presentationOriginalAtom);

  const setWithHistory = useCallback(
    (next: PresentationData) => {
      if (!presentation) {
        setPresentation(next);
        return;
      }
      setHistory((prev) => ({
        past: [...prev.past, presentation].slice(-MAX_HISTORY),
        future: [],
      }));
      setPresentation(next);
    },
    [presentation, setPresentation, setHistory]
  );

  const undo = useCallback(() => {
    if (history.past.length === 0 || !presentation) return;
    const previous = history.past[history.past.length - 1];
    if (!previous) return;
    setHistory((prev) => ({
      past: prev.past.slice(0, -1),
      future: [presentation, ...prev.future],
    }));
    setPresentation(previous);
  }, [history.past, presentation, setHistory, setPresentation]);

  const redo = useCallback(() => {
    if (history.future.length === 0 || !presentation) return;
    const next = history.future[0];
    if (!next) return;
    setHistory((prev) => ({
      past: [...prev.past, presentation],
      future: prev.future.slice(1),
    }));
    setPresentation(next);
  }, [history.future, presentation, setHistory, setPresentation]);

  const discardAll = useCallback(() => {
    if (!original) return;
    setPresentation(original);
    setHistory({ past: [], future: [] });
  }, [original, setPresentation, setHistory]);

  return {
    setWithHistory,
    undo,
    redo,
    discardAll,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    hasChanges: history.past.length > 0,
  };
}
